"use client";

import * as React from "react";
import { Eye } from "lucide-react";
import type { Citation } from "@quovy/field-types";
import { Button } from "../../inputs/button";
import { cn } from "../../utils";

export interface CitationListProps {
  citations: Citation[];
  primaryCitationId?: string | null;
  /**
   * Optional field-type name. Reserved for future use (e.g. resolving a
   * registry formatter for snapshot labels). The current implementation
   * prefers `citation.label` and does not invoke the formatter.
   */
  valueType?: string;
  onCitationSelect?: (id: string) => void;
  onCitationView?: (id: string) => void;
  className?: string;
}

/**
 * Shared citation list rendered inside inline-edit popovers (and other
 * citation surfaces). Uses the canonical `Citation` shape from
 * `@quovy/field-types`. Replaces the two hand-rolled lists that previously
 * lived inside `cell-inline-edit.tsx` and `field-value.tsx`.
 */
export function CitationList({
  citations,
  primaryCitationId,
  onCitationSelect,
  onCitationView,
  className,
}: CitationListProps) {
  if (!citations || citations.length === 0) return null;
  return (
    <div className={cn("max-h-64 overflow-y-auto", className)}>
      {citations.map((citation) => {
        const isPrimary = primaryCitationId === citation.id;
        const selectable = Boolean(onCitationSelect);
        const rowProps: React.HTMLAttributes<HTMLDivElement> & {
          role?: string;
          tabIndex?: number;
        } = selectable
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => onCitationSelect?.(citation.id),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onCitationSelect?.(citation.id);
                }
              },
            }
          : {};
        return (
          <div
            key={citation.id}
            {...rowProps}
            aria-current={isPrimary ? "true" : undefined}
            className={cn(
              "relative flex w-full items-start gap-2 px-3 py-2 text-left",
              // Left-edge accent stripe marks the primary citation without
              // stealing horizontal space from the value/source text.
              "before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r-sm before:content-['']",
              isPrimary ? "before:bg-foreground" : "before:bg-transparent",
              selectable && "transition-colors hover:bg-muted/50",
              isPrimary && "bg-muted/40",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium text-foreground">
                  {citation.snippet ?? citation.label}
                </span>
              </div>
              {citation.snippet && (
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-xs text-muted-foreground">{citation.label}</span>
                  {citation.page != null && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      · p.{citation.page}
                    </span>
                  )}
                </div>
              )}
              {!citation.snippet && citation.page != null && (
                <div className="flex items-center gap-1.5">
                  <span className="shrink-0 text-xs text-muted-foreground">p.{citation.page}</span>
                </div>
              )}
            </div>
            {onCitationView && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-0.5 size-5 shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
                aria-label="View citation source"
                onClick={(e) => {
                  e.stopPropagation();
                  onCitationView(citation.id);
                }}
              >
                <Eye className="size-3" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
